import axios from "axios";
import { DataGrid } from "devextreme-react";
import {
  Column,
  Editing,
  Form,
  LoadPanel,
  Popup,
  Scrolling,
  SearchPanel,
  Selection,
  Texts,
  Toolbar,
} from "devextreme-react/data-grid";
import { Label, RequiredRule, SimpleItem } from "devextreme-react/form";
import { Item } from "devextreme-react/toolbar";
import CustomStore from "devextreme/data/custom_store";
import React, { useEffect, useRef, useState } from "react";
import { useSelector } from "react-redux";
import ExperienceStudyRow from "./ExperienceStudyRow";
import StudyDropDownBox from "./StudyDropDownBox";

export default function ExperienceStudies() {
  const [dataSource, setDataSource] = useState(null);

  const userInfo = useSelector((state) => state.userInfo);

  const experienceStudiesGridRef = useRef(null);
  const studyDropDownBoxRef = useRef(null);

  useEffect(() => {
    setDataSource(
      new CustomStore({
        key: "SEQ",
        load: async () => {
          const result = await axios.get(
            `${process.env.REACT_APP_BECCO_API_URL}/api/cv/experienceStudies/${userInfo.employeeNo}`
          );

          return result.data;
        },
        insert: async (values) => {
          await axios.post(
            `${process.env.REACT_APP_BECCO_API_URL}/api/cv/experienceStudies/${userInfo.employeeNo}`,
            {
              ...values,
              id: studyDropDownBoxRef.current.instance.option("value"),
            }
          );
        },
        update: async (key, values) => {
          await axios.put(
            `${process.env.REACT_APP_BECCO_API_URL}/api/cv/experienceStudies/${userInfo.employeeNo}/${key}`,
            {
              ...values,
              id: studyDropDownBoxRef.current.instance.option("value"),
            }
          );
        },
        remove: async (key) => {
          await axios.delete(
            `${process.env.REACT_APP_BECCO_API_URL}/api/cv/experienceStudies/${userInfo.employeeNo}/${key}`
          );
        },
      })
    );
  }, []);

  const onEditingStart = (e) => {
    e.component.selectRows(e.key, true);
  };

  const onSaving = React.useCallback(async (e) => {
    if (e.changes.length == 0) {
      const SEQ =
        experienceStudiesGridRef.current.instance.option("editing.editRowKey");

      await axios.put(
        `${process.env.REACT_APP_BECCO_API_URL}/api/cv/experienceStudies/${userInfo.employeeNo}/${SEQ}`,
        {
          id: studyDropDownBoxRef.current.instance.option("value"),
        }
      );

      experienceStudiesGridRef.current.instance.refresh();
    }
  });

  return (
    <DataGrid
      id="experienceStudiesGrid"
      ref={experienceStudiesGridRef}
      dataSource={dataSource}
      rowAlternationEnabled={true}
      showBorders={true}
      showColumnLines={true}
      allowColumnResizing={true}
      columnAutoWidth={true}
      wordWrapEnabled={true}
      allowColumnReordering={true}
      style={{
        maxHeight: "calc(100vh - 180px)",
        marginLeft: "10px",
        marginRight: "10px",
        // marginTop: "56px",
      }}
      hoverStateEnabled={true}
      //   showColumnHeaders={false}
      //   dataRowRender={ExperienceStudyRow}
      onEditingStart={onEditingStart}
      onSaving={onSaving}
    >
      <LoadPanel enabled={true} />

      <SearchPanel visible={true} />

      <Scrolling mode={"virtual"} showScrollbar={"always"} />

      <Selection mode={"single"} />

      <Editing
        mode={"popup"}
        allowAdding={true}
        allowDeleting={true}
        allowUpdating={true}
      >
        <Texts
          confirmDeleteTitle={"Delete the Experience Studies"}
          confirmDeleteMessage={
            "Are you sure you want to delete this experience study?"
          }
        />
        <Popup title={"Experience Study"} showTitle={true} height={250} />
        <Form
          labelMode={"outside"}
          showColonAfterLabel={false}
          labelLocation="top"
          minColWidth={300}
          colCount={2}
          colCountByScreen={{ sm: 2, md: 4 }}
          screenByWidth={(width) => {
            return width < 720 ? "sm" : "md";
          }}
          validationGroup="experienceStudies"
          height={"100%"}
        >
          <SimpleItem colSpan={4} dataField="id">
            <Label text={"Study"} />
            <RequiredRule message={"* Study를 입력해주세요."} />
            <StudyDropDownBox
              experienceStudiesGridRef={experienceStudiesGridRef}
              studyDropDownBoxRef={studyDropDownBoxRef}
            />
          </SimpleItem>
          <SimpleItem
            dataField={"count"}
            editorType="dxNumberBox"
            editorOptions={{ showClearButton: true }}
          >
            <Label text={"Count"} />
          </SimpleItem>
          <SimpleItem
            dataField={"role"}
            editorType="dxTextBox"
            editorOptions={{ showClearButton: true }}
          >
            <Label text={"Role"} />
            <RequiredRule message={"* Role을 입력해주세요."} />
          </SimpleItem>
          <SimpleItem
            dataField={"details"}
            colSpan={2}
            editorType="dxTextBox"
            editorOptions={{ showClearButton: true }}
          >
            <Label text={"Details"} />
          </SimpleItem>
        </Form>
      </Editing>

      <Column dataField={"id"} visible={false} />
      <Column dataField={"APPLY_ENTP_NAME"} caption="Sponsor" />
      <Column dataField={"CLINIC_EXAM_TITLE"} caption="Title" />
      <Column dataField={"TRGT_DISS_CD_NM"} caption="Indication" />
      <Column dataField={"CLINIC_STEP_NAME"} caption="Phase" />
      <Column dataField={"count"} caption="Count" />
      <Column dataField={"role"} caption="Role" />
      <Column dataField={"details"} caption="Details" />

      <Toolbar>
        <Item location={"before"}>
          <span
            style={{ fontWeight: 500, color: "#009688", marginLeft: "10px" }}
          >
            EXPERIENCE STUDIES
          </span>
        </Item>
        <Item location="after" name="addRowButton" />
        <Item location="after" name="searchPanel" />
      </Toolbar>
    </DataGrid>
  );
}
